import React, { useEffect, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { IoIosArrowRoundBack, IoMdEye } from "react-icons/io";
import { MdFilterAlt } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Pagination from "../../../components/common/Pagination";
import { FiEdit, FiTrash } from "react-icons/fi";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  updateOrderStatus,
  getOrderDetail,
  deleteOrder,
  getAllOrders,
} from "../../../api/owner/hotels/orders";
import socket from "../../../helper/socket";
import { useTranslation } from "../../../hooks/useTranslation";

// Popup Modal Component
const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[978px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const ForRestaurants = () => {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const statuses = [
    t("forRestaurants").statuses.confirmed,
    t("forRestaurants").statuses.pending,
    t("forRestaurants").statuses.cancelled,
  ];

  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 10;
  const navigate = useNavigate();
  const [selectedOrderId, setselectedOrderId] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [formData, setFormData] = useState({
    tableNo: "",
    discountPercentage: "",
    tokenCharges: "",
    orderStatus: "",
  });

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page when searching
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Map status filter to backend format
  const getStatusFilter = () => {
    if (!status) return "";
    const statusMap = {
      [t("forRestaurants").statuses.confirmed]: "confirmed",
      [t("forRestaurants").statuses.pending]: "pending",
      [t("forRestaurants").statuses.cancelled]: "cancelled",
    };
    return statusMap[status] || "";
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["orders", page, limit, debouncedSearch, getStatusFilter()],
    queryFn: () => getAllOrders({ page, limit, search: debouncedSearch, status: getStatusFilter() }),
    keepPreviousData: true,
  });

  const { data: OrdersDetailData, isLoading: isOrderDetailLoading } = useQuery({
    queryKey: ["order-detail", selectedOrderId],
    queryFn: () => getOrderDetail(selectedOrderId),
    enabled: !!selectedOrderId && isPopupOpen,
    keepPreviousData: true,
  });

  const orders = data?.data?.orders || [];
  const pagination = data?.data?.pagination || {};
  const orderDetail = OrdersDetailData?.data?.order;

  const updateMutation = useMutation({
    mutationFn: (data) => updateOrderStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["orders"]);
      queryClient.invalidateQueries(["order-detail"]);
      toast.success(t("forRestaurants").messages.updateSuccess);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("forRestaurants").messages.updateError;
      toast.error(errorMessage);
    },
  });

  const deleteBookingMutation = useMutation({
    mutationFn: (data) => deleteOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["orders"]);
      toast.success(t("forRestaurants").messages.deleteSuccess);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("forRestaurants").messages.deleteError;
      toast.error(errorMessage);
    },
  });

  useEffect(() => {
    if (orderDetail?.orderStatus) {
      setFormData((prev) => ({
        ...prev,
        orderStatus: orderDetail?.orderStatus,
      }));
    }
  }, [orderDetail]);

  useEffect(() => {
    const handleNewBooking = () => {
      refetch();
    };

    socket.on("restaurantOrder", handleNewBooking);

    return () => {
      socket.off("restaurantOrder", handleNewBooking);
    };
  }, [refetch]);

  const handleFilterChange = (value) => {
    setStatus(value);
    setIsOpen(false);
    setPage(1); // Reset to first page when filtering
  };

  const openModel = (id) => {
    setselectedOrderId(id);
    setIsPopupOpen(true);
  };

  const handleSubmit = () => {
    updateMutation.mutate({
      orderId: selectedOrderId,
      status: 'confirmed',
    });
  };

  const handleCancel = () => {
    Swal.fire({
      title: t("forRestaurants").confirmDialog.cancelTitle,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("forRestaurants").confirmDialog.cancelConfirmBtn,
      cancelButtonText: t("forRestaurants").confirmDialog.cancelDenyBtn,
    }).then((result) => {
      if (result.isConfirmed) {
        updateMutation.mutate({
          orderId: selectedOrderId,
          status: 'cancelled',
        });
      }
    });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t("forRestaurants").confirmDialog.deleteTitle,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("forRestaurants").confirmDialog.deleteConfirmBtn,
      cancelButtonText: t("forRestaurants").confirmDialog.deleteDenyBtn,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteBookingMutation.mutate(id);
      }
    });
  };

  const handleUpdateStatus = () => {
    updateMutation.mutate({
      orderId: selectedOrderId,
      status: formData.orderStatus,
    });
  };

  function convertToAmPm(timeStr) {
    if (!timeStr || typeof timeStr !== "string" || !timeStr.includes(":"))
      return "";

    const [hour, minute] = timeStr.split(":").map(Number);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
  }

  return (
    <>
      <div className="flex justify-between items-center flex-wrap gap-[20px]">
        <div className="flex gap-[10px] items-center">
          <div className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]">
            <IoIosArrowRoundBack
              onClick={() => navigate(-1)}
              size={24}
              className="text-[#fff]"
            />
          </div>
          <h2 className="text-[22px] font-medium">{t("orders").title}</h2>
        </div>
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="py-[9px] px-[35px] bg-white text-base  font-medium flex  items-center gap-[6px] rounded-[2px] border border-[#DDDDDD]"
          >
            <MdFilterAlt /> {status || t("orders").filter}
          </button>
          {isOpen && (
            <div className="absolute z-10 mt-2 w-36 bg-white border rounded shadow text-sm font-medium">
              {statuses.map((item) => (
                <div
                  key={item}
                  onClick={() => handleFilterChange(item)}
                  className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                >
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-[31px]">
        <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[28px] py-[20px] sm:px-[30px] px-[15px] bg-white">
          <div className="flex justify-between items-center py-[13px] flex-wrap  gap-[10px]">
            <h2 className="text-base font-semibold">{t("orders").title}</h2>
            <div className="relative w-full max-w-[301px]">
              <span className="absolute inset-y-0 left-3 flex items-center">
                <CiSearch size={24} className="text-[#4CAF50]" />
              </span>
              <input
                type="text"
                placeholder={t("orders").searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full border border-[#DDDDDD] focus:outline-none focus:border-[#4CAF50]"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : isError ? (
            <p className="text-red-500 text-center mt-6">{error.message}</p>
          ) : (
            <div className="overflow-x-auto md:mt-[30px] mt-[20px] grid">
              <table className="min-w-full  ">
                <thead className="bg-[#FFFAF2] ">
                  <tr className="">
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").id}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").hotelName}
                    </th>
                    {/* <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                    Date&Time
                  </th> */}

                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").roomNo}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").customer}
                    </th>

                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").items}
                    </th>
                    {/* <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] max-w-[197px]">
                    Description
                  </th> */}
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").qty}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").price}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").payment}
                    </th>

                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").orderStatus}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[16px] whitespace-nowrap">
                      {t("orders").actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders?.length > 0 &&
                    orders?.map((order) => {
                      return (
                        <tr className="border-t border-[#E4E4E4] ">
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?._id}
                          </td>

                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.hotelName || order?.bookingId?.hotelId?.name || order?.hotelId?.name || '-'}
                          </td>
                          {/* <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                        {order?.reservationId?.bookingDate && new Date(order?.reservationId?.bookingDate).toLocaleDateString('en-GB')}
                      </td> */}
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.roomNumbers}
                          </td>
                          <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                            {order?.userId?.name}
                          </td>
                          <td className="py-[20px] px-[16px] text-base font-normal text-black whitespace-nowrap">
                            {order?.items?.map((item, index) => (
                              <div key={index}>
                                {item.quantity} x {item.itemId?.itemName}
                              </div>
                            ))}
                          </td>

                          {/* <td className="py-[20px] px-[16px] text-base font-normal text-black  whitespace-nowrap">
                        {order?.notes}
                      </td> */}
                          <td className="py-[20px] px-[16px] text-base font-normal text-black whitespace-nowrap">
                            {order?.items?.reduce(
                              (total, item) => total + item.quantity,
                              0
                            )}
                          </td>

                          <td className="py-[20px] px-[16px]">
                            ₾{order?.totalAmount}
                          </td>

                          <td className="py-[20px] px-[16px] ">
                            {order?.paymentStatus}
                          </td>

                          <td className="py-[20px] px-[16px]">
                            <div
                              className={`px-[28px] py-2 w-max font-normal rounded-full text-center text-sm 
    ${
      order?.orderStatus?.toLowerCase() === "pending" ||
      order?.orderStatus?.toLowerCase() === "placed"
        ? "bg-yellow-500 text-white" // Pending or Placed: Yellow
        : order?.orderStatus?.toLowerCase() === "confirmed"
        ? "bg-[#4CAF50] text-white" // Confirmed: Green
        : order?.orderStatus?.toLowerCase() === "preparing"
        ? "bg-blue-500 text-white" // Preparing: Blue
        : order?.orderStatus?.toLowerCase() === "ready"
        ? "bg-purple-500 text-white" // Ready: Purple
        : order?.orderStatus?.toLowerCase() === "served"
        ? "bg-teal-500 text-white" // Served: Teal
        : order?.orderStatus?.toLowerCase() === "cancelled"
        ? "bg-red-500 text-white" // Cancelled: Red
        : order?.orderStatus?.toLowerCase() === "completed"
        ? "bg-gray-500 text-white" // Completed: Gray
        : "bg-gray-300 text-black" // Default color for any unknown status
    }
  `}
                            >
                              {order?.orderStatus}
                            </div>
                          </td>

                          <td className="py-[20px] px-[16px]  flex gap-[12px]">
                            <a
                              href="#"
                              onClick={() => openModel(order?._id)}
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                            >
                              <IoMdEye className="text-base text-black" />
                            </a>

                            <button
                              onClick={() => handleDelete(order?._id)}
                              className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2] "
                            >
                              <RiDeleteBin6Line className="text-base text-black" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
          {orders?.length > 0 && (
            <Pagination
              page={pagination.currentPage}
              totalPages={pagination.totalPages}
              setPage={setPage}
            />
          )}
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="w-full rounded-[20px] md:p-[30px] py-[20px] px-[15px]">
          <div className="grid grid-col-1 sm:grid-cols-3 justify-between text-left pb-[21px] border-b border-[#EEEEEE] mb-[18px]">
            <h2 className="text-[22px] font-medium col-span-2">
              {t("orders").orderDetail}
            </h2>

            {orderDetail?.status === "pending" && (
              <div className="flex items-center gap-[20px]">
                <h2 className="text-lg font-medium">
                  {t("restaurantreservation").reservationInformation}
                </h2>
                <div
                  onClick={() => setEnabled(!enabled)}
                  className={`w-[48px] h-[20px] flex items-center rounded-full p-1 cursor-pointer transition-colors ${
                    enabled ? "bg-blue-500" : "bg-[#e1e1e1]"
                  }`}
                >
                  <div
                    className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                      enabled ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="h-[350px] overflow-y-auto">
            <p className="text-[17px] font-medium mb-[15px]">
              {t("orders").forOrder} ID: {orderDetail?._id} -{" "}
              {orderDetail?.userId?.name}
              {t("orders").reservation}
            </p>
            <div className="border border-[#DDDDDD] rounded-[5px] xl:gap-[47px] gap-[20px] p-[20px] pb-[36px] bg-white grid lg:grid-cols-[auto_auto_1fr] md:grid-cols-2 grid-cols-1">
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("restaurantreservation").reservationStatus}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">{t("orders").id}:</span>
                    {orderDetail?._id}
                  </li>

                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">{t("orders").name}:</span>
                    {orderDetail?.userId?.name}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">{t("orders").items}:</span>
                    {orderDetail?.items?.map((item, index) => (
                      <div key={index}>
                        {item.quantity} x {item.itemId?.itemName}
                      </div>
                    ))}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">{t("orders").checkIn}:</span>{" "}
                    {orderDetail?.bookingId?.checkInDate &&
                      new Date(
                        orderDetail?.bookingId?.checkInDate
                      ).toLocaleDateString("en-GB")}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">{t("orders").checkOut}:</span>{" "}
                    {orderDetail?.bookingId?.checkOutDate &&
                      new Date(
                        orderDetail?.bookingId?.checkOutDate
                      ).toLocaleDateString("en-GB")}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("orders").orderPlaced}:
                    </span>
                    {orderDetail?.createdAt &&
                      new Date(orderDetail.createdAt).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("restaurantreservation").paymentInformation}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">
                      {t("orders").paymentMethod}:
                    </span>
                    {orderDetail?.bookingId?.paymentMethod}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("orders").paymentStatus}:
                    </span>{" "}
                    {orderDetail?.bookingId?.paymentStatus}
                  </li>
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">{t("orders").subTotal}:</span>{" "}
                    ₾{orderDetail?.subtotal}
                  </li>
                  {/* <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">Token Charges :</span>₾{orderDetail?.
                      tokenCharges}
                  </li> */}
                  {/* <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">Discount:</span> ₾{orderDetail?.discountAmount
                    } ({orderDetail?.reservationId?.discountPercentage}%)

                  </li> */}
                  <li className="text-[17px] text-[#757474] mb-[10px]">
                    <span className="font-medium">{t("orders").total}:</span> ₾
                    {orderDetail?.totalAmount}
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="text-[17px] font-medium text-black">
                  {t("orders").orderStatus}
                </h2>
                <ul className="mt-5">
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("orders").currentStatus}:
                    </span>{" "}
                    {orderDetail?.orderStatus}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("orders").deliveryTime}:
                    </span>{" "}
                    {orderDetail?.deliveryTime
                      ? new Date(
                          `1970-01-01T${orderDetail.deliveryTime}`
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "N/A"}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("orders").deliveryMethod}:
                    </span>
                    {orderDetail?.deliveryMethod}
                    {orderDetail?.tableNo}
                  </li>
                  <li className="text-[17px]  mb-[10px] text-[#FF9D00]">
                    <span className="font-medium text-[#757474]">
                      {t("orders").notes}:
                    </span>{" "}
                    {orderDetail?.notes}
                  </li>
                </ul>
              </div>
            </div>

            {orderDetail?.orderStatus !== "pending" && (
              <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-[20px] mt-5">
                <div className="max-w-[473px]">
                  <h2 className="text-[17px] font-medium mb-[10px]">
                    {t("orders").updateOrderStatusTitle}
                  </h2>
                  <p className="text-[15px] text-[#605D5D] font-normal">
                    {t("orders").updateOrderStatusDescription}
                  </p>
                </div>
                <div className="w-full sm:max-w-[324px] sm:ml-auto">
                  <h2 className="text-[17px] font-medium mb-[10px]">
                    {t("orders").orderStatusTitle}
                  </h2>
                  <div className="flex items-center sm:flex-row flex-col gap-[10px]">
                    <div className="flex border border-[#DDDDDD] rounded-md overflow-hidden w-full sm:max-w-[184px]">
                      <select
                        name="orderStatus"
                        value={formData.orderStatus}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            orderStatus: e.target.value,
                          }))
                        }
                        className={`border border-[#DDDDDD]
                         rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50]`}
                      >
                        <option value="preparing">
                          {t("orders").statusPreparing}
                        </option>
                        <option value="ready">{t("orders").statusReady}</option>
                        <option value="served">
                          {t("orders").statusServed}
                        </option>
                        <option value="cancelled">
                          {t("orders").statusCancelled}
                        </option>
                        <option value="confirmed">
                          {t("orders").statusConfirmed}
                        </option>
                        <option value="completed">
                          {t("orders").statusCompleted}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {orderDetail?.orderStatus === "pending" ? (
            <div className="flex sm:flex-row flex-col md:gap-[30px] gap-[10px] justify-end pt-[23px] mt-[30px] border-t border-[#EEEEEE]">
              <button
                onClick={handleSubmit}
                className="inline-block sm:w-max w-full rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold py-[14px] px-[27px] green-shadow hover:bg-[#209b25]"
              >
                {t("orders").confirmOrder}
              </button>
              <button
                onClick={handleCancel}
                className="text-white  sm:w-max w-full font-bold inline-block py-[14px] px-[15px] rounded-[2px] bg-[#FF0000] hover:bg-[#9B2020] 2xl:text-[20px] text-base text-center red-shadow"
              >
                {t("orders").cancelOrder}
              </button>
            </div>
          ) : (
            <div className="flex sm:flex-row flex-col md:gap-[30px] gap-[10px] justify-end pt-[23px] mt-[30px] border-t border-[#EEEEEE]">
              <button
                onClick={handleUpdateStatus}
                className="inline-block sm:w-max w-full rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold py-[14px] px-[27px] green-shadow hover:bg-[#209b25] mt-4"
              >
                {t("orders").updateOrderStatusButton}
              </button>
            </div>
          )}
        </div>
      </PopupModal>
    </>
  );
};

export default ForRestaurants;
